import { UpdateFlexibleWorkingHoursDocument, type UpdateFlexibleWorkingHoursMutation, } from "owa-calendar-working-hours-option/lib/graphql/__generated__/updateFlexibleWorkingHoursMutation.interface";
import getWorkLocationDetails from "./getWorkLocationDetails";
import type { MailboxInfo } from "owa-client-types";
import { getUserEmailAddress } from "accelerator-aad/lib/getUserEmailAddress";
import type { FlexibleWorkingHoursInput, EnhancedLocation, ResolverContext, WorkLocationType, UpdateFlexibleWorkingHoursRequest, LocationSourceType, LocationIdType, LocationEntityType, Exact, DayOfWeek, } from "owa-graph-schema";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";

export default function updateRecurringFlexibleWorkHours(mailboxInfo: MailboxInfo, oldSegment: FlexibleWorkingHoursInput): {
    mutation: TypedDocumentNode<UpdateFlexibleWorkingHoursMutation, Exact<{
        input: UpdateFlexibleWorkingHoursRequest;
    }>>;
    variables: {
        input: {
            mailboxId: string;
            updateDetails: {
                segment: {
                    id: string;
                    start: {
                        dateTime: string;
                        timeZone: {
                            name: string;
                        };
                    };
                    end: {
                        dateTime: string;
                        timeZone: {
                            name: string;
                        };
                    };
                    workLocationType: WorkLocationType;
                    recurrenceDetailsWH: {
                        recurrence: {
                            recurrencePattern: {
                                weeklyRecurrencePattern: {
                                    firstDayOfWeek: DayOfWeek;
                                    daysOfWeek: DayOfWeek[];
                                    interval: number;
                                };
                            };
                        };
                        timeZone: {
                            name: string;
                        };
                    };
                    workLocationDetails: {
                        displayName: string;
                        annotation: string;
                        postalAddress: {
                            street: string;
                            city: string;
                            state: string;
                            country: string;
                            postalCode: string;
                            postOfficeBox: string;
                        };
                        id: string;
                        idType: LocationIdType;
                        locationUri: string;
                        locationType: LocationEntityType;
                        locationSource: LocationSourceType;
                        coordinates: {
                            longitude: number;
                            latitude: number;
                            accuracy: number;
                            altitude: number;
                            altitudeAccuracy: number;
                        };
                    } | null;
                };
            };
        };
    };
    context: ResolverContext | undefined;
} {
    const isOffice: boolean = oldSegment?.workLocationType === "Office";
    //// Add the dropdown selection for the building to save the value
    const workLocationDetails = getWorkLocationDetails(isOffice, {
        Id: oldSegment?.workLocationDetails?.id,
        DisplayName: oldSegment?.workLocationDetails?.displayName,
    } as EnhancedLocation);
    
    const isMultiAccountEnabled = isGlobalFeatureEnabled("cal-surface-places-delegate") ||
        isGlobalFeatureEnabled("cal-surface-places-multi-account");
    
    const context = isMultiAccountEnabled
        ? ({
            requestOptions: {
                mailboxInfo,
            },
        } as ResolverContext)
        : undefined;
    
    return {
        mutation: UpdateFlexibleWorkingHoursDocument,
        variables: {
            input: {
                mailboxId: isMultiAccountEnabled
                    ? mailboxInfo.mailboxSmtpAddress
                    : getUserEmailAddress(),
                updateDetails: {
                    segment: {
                        id: oldSegment.id,
                        start: {
                            dateTime: oldSegment.start.dateTime,
                            timeZone: {
                                name: oldSegment.start.timeZone.name,
                            },
                        },
                        end: {
                            dateTime: oldSegment.end.dateTime,
                            timeZone: {
                                name: oldSegment.start.timeZone.name,
                            },
                        },
                        workLocationType: oldSegment.workLocationType,
                        recurrenceDetailsWH: {
                            recurrence: {
                                recurrencePattern: {
                                    weeklyRecurrencePattern: {
                                        firstDayOfWeek: oldSegment.recurrenceDetailsWH?.recurrence
                                            .recurrencePattern?.weeklyRecurrencePattern
                                            .firstDayOfWeek ?? "Sunday",
                                        daysOfWeek: oldSegment.recurrenceDetailsWH?.recurrence
                                            .recurrencePattern?.weeklyRecurrencePattern
                                            .daysOfWeek ?? [],
                                        interval: 1,
                                    },
                                },
                            },
                            timeZone: {
                                name: oldSegment.recurrenceDetailsWH?.timeZone.name || "UTC",
                            },
                        },
                        workLocationDetails,
                    },
                },
            },
        },
        context,
    };
}
